import {
  useIsMobile,
  WSAvatar,
  WSButton,
  WSCard,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useOnVisible } from "../../../../shared/hooks/useOnVisible";

import camelCase from "lodash/camelCase";
import { useQueryMemberPublicFile } from "../../../../query/files/queries";
import styles from "./styles.module.scss";

type TodoItemProps = {
  onClick?(): void;
  viewed?: boolean;
  loading?: boolean;
  title: string;
  body: React.ReactNode;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  buttonText?: string;
  isPending?: boolean;
  onDismiss?(): Promise<any>;
  onRead?(): void;
  buttonAction?(): void;
  buttonActionAsync?(): Promise<void>;
  numberOfTasks?: number;
  company?: {
    name: string;
    logoFileId: string;
  };
};

export const TodoItem: React.FC<TodoItemProps> = ({
  viewed,
  loading,
  body,
  footer,
  header,
  buttonText,
  buttonAction,
  buttonActionAsync,
  numberOfTasks,
  company,
  onDismiss,
  onRead,
  title,
  onClick,
  isPending
}) => {
  const [isViewed, setIsViewed] = React.useState(viewed || !onRead);
  const [ref] = useOnVisible(() => {
    setIsViewed(true);
    onRead?.();
  }, isViewed);
  const [processing, setProcessing] = React.useState(false);
  const [logoURL, setLogoURL] = React.useState<string>();

  useQueryMemberPublicFile(company?.logoFileId || "", {
    enabled: !!company?.logoFileId,
    onSuccess: response => {
      setLogoURL(response.url);
    }
  });
  const isMobile = useIsMobile();

  return (
    <WSCard
      shimmer={loading || processing}
      onClick={isPending ? undefined : onClick}
      as="label"
      data-testid={`todoItem-${camelCase(title)}`}
      className={styles.card}
    >
      <WSElement ref={ref}>
        <WSFlexBox.CenterY
          wrap="nowrap"
          justify="space-between"
          alignItems="center"
        >
          {header ? (
            header
          ) : (
            <WSFlexBox.CenterY wrap="nowrap" gap="M" alignItems="center">
              <WSAvatar
                type="icon"
                icon="clipboard-check"
                badge={isViewed ? undefined : true}
              />

              <WSText.Heading5>{title}</WSText.Heading5>
              {isPending ? (
                isMobile ? (
                  <WSPill icon="time" />
                ) : (
                  <WSPill text="Pending" icon="time" />
                )
              ) : !onDismiss &&
                (buttonAction || buttonActionAsync || footer) ? (
                isMobile ? (
                  <WSPill icon theme="warning" />
                ) : (
                  <WSPill text="Action required" icon theme="warning" />
                )
              ) : null}
            </WSFlexBox.CenterY>
          )}

          <WSFlexBox.CenterY wrap="nowrap" ml="M">
            {numberOfTasks ? (
              <WSFlexBox.CenterY wrap="nowrap">
                <WSPill
                  text={
                    numberOfTasks >= 50
                      ? `${numberOfTasks}+`
                      : String(numberOfTasks)
                  }
                />
              </WSFlexBox.CenterY>
            ) : company ? (
              <WSFlexBox.CenterY wrap="nowrap">
                {logoURL ? <WSAvatar.Image size="S" image={logoURL} /> : null}
                {company.name ? (
                  <WSText.ParagraphSm ml="XS" pl="XS" color="gray500">
                    {company.name}
                  </WSText.ParagraphSm>
                ) : null}
              </WSFlexBox.CenterY>
            ) : null}
            {onDismiss ? (
              <WSIcon
                block
                onClick={async event => {
                  event.stopPropagation();
                  event.preventDefault();

                  setProcessing(true);
                  await onDismiss();
                  setProcessing(false);
                }}
                size="XS"
                ml="M"
                name="exit"
                color="gray500"
              />
            ) : null}
          </WSFlexBox.CenterY>
        </WSFlexBox.CenterY>
        <WSText.ParagraphSm color="gray500" className={styles.body}>
          {body}
        </WSText.ParagraphSm>
        {isPending ? null : footer ? (
          <WSElement mt="M">{footer}</WSElement>
        ) : buttonText ? (
          <WSElement mt="M">
            <WSButton.Link
              size="S"
              onClick={buttonAction}
              onAsyncClick={buttonActionAsync}
              rightIcon="chevron-right"
            >
              {buttonText}
            </WSButton.Link>
          </WSElement>
        ) : null}
      </WSElement>
    </WSCard>
  );
};
